import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Rectangle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { BoxSelect, X, Check } from "lucide-react";

// Fix default marker icon in react-leaflet + webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const colorIcon = (color) => L.divIcon({
  className: "custom-marker",
  html: `<div style="background:${color};width:14px;height:14px;border-radius:50%;border:2px solid white;box-shadow:0 0 0 2px ${color}55;"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const selectedIcon = (color) => L.divIcon({
  className: "custom-marker",
  html: `<div style="background:${color};width:18px;height:18px;border-radius:50%;border:3px solid #0020C2;box-shadow:0 0 0 3px rgba(0,32,194,.35);"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const COMPANY_COLORS = ["#0020C2", "#16A34A", "#DC2626", "#F59E0B", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];

// Toplu seçim modunda haritada dikdörtgen çizerek alan seçimi
const RectSelect = ({ enabled, onSelectBounds }) => {
  const map = useMap();
  const startRef = useRef(null); // { latlng, point }
  const [bounds, setBounds] = useState(null);

  useEffect(() => {
    if (!enabled) return;
    map.dragging.disable();
    map.doubleClickZoom.disable();
    const el = map.getContainer();
    el.style.cursor = "crosshair";
    return () => { map.dragging.enable(); map.doubleClickZoom.enable(); el.style.cursor = ""; };
  }, [enabled, map]);

  useMapEvents({
    mousedown(e) { if (!enabled) return; startRef.current = { latlng: e.latlng, point: e.containerPoint }; setBounds(null); },
    mousemove(e) { if (!enabled || !startRef.current) return; setBounds(L.latLngBounds(startRef.current.latlng, e.latlng)); },
    mouseup(e) {
      if (!enabled || !startRef.current) return;
      const dist = startRef.current.point.distanceTo(e.containerPoint);
      const b = L.latLngBounds(startRef.current.latlng, e.latlng);
      startRef.current = null;
      setBounds(null);
      if (dist > 10) onSelectBounds(b);
    },
  });

  return enabled && bounds ? <Rectangle bounds={bounds} pathOptions={{ color: "#0020C2", weight: 1.5, dashArray: "4 4", fillOpacity: 0.08 }} /> : null;
};

export default function PersonnelMap({ personnel = [], height = 500, onFix = null, onDelete = null, onBulkCompany = null }) {
  const withCoords = useMemo(() => personnel.filter(p => typeof p.lat === "number" && typeof p.lng === "number"), [personnel]);
  const companies = useMemo(() => [...new Set(personnel.map(p => p.company_name))].filter(Boolean), [personnel]);
  const colorFor = (company) => COMPANY_COLORS[companies.indexOf(company) % COMPANY_COLORS.length];
  const [hiddenCompanies, setHiddenCompanies] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [targetCompany, setTargetCompany] = useState("");
  const [newCompany, setNewCompany] = useState("");
  const [applying, setApplying] = useState(false);

  const toggleCompany = (c) => {
    setHiddenCompanies(prev => {
      const s = new Set(prev);
      if (s.has(c)) s.delete(c); else s.add(c);
      return s;
    });
  };
  const showOnly = (c) => {
    // Çift tıklama: sadece bu firmayı göster (veya tümünü geri aç)
    setHiddenCompanies(prev =>
      prev.size === companies.length - 1 && !prev.has(c)
        ? new Set()
        : new Set(companies.filter(x => x !== c))
    );
  };
  const visible = withCoords.filter(p => !hiddenCompanies.has(p.company_name));

  const toggleSelectMode = () => {
    setSelectMode(m => !m);
    setSelected(new Set());
  };

  const togglePerson = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const selectInBounds = (b) => {
    const ids = visible.filter(p => b.contains([p.lat, p.lng])).map(p => p.id);
    if (ids.length > 0) setSelected(prev => new Set([...prev, ...ids]));
  };

  const applyCompany = async () => {
    const company = (newCompany.trim() || targetCompany).trim();
    if (!company || selected.size === 0) return;
    setApplying(true);
    try {
      await onBulkCompany([...selected], company);
      setSelected(new Set());
      setNewCompany("");
    } catch {
      // iptal veya hata — seçim korunur
    } finally {
      setApplying(false);
    }
  };

  // Center default: Ankara Turkey
  const center = withCoords.length > 0
    ? [withCoords[0].lat, withCoords[0].lng]
    : [39.9334, 32.8597];

  if (withCoords.length === 0) {
    return (
      <div className="bg-white border border-[#E5E5E5] p-16 text-center rounded-md" style={{ height }}>
        <div className="text-neutral-500 text-sm">Haritada gösterilecek koordinatı olan personel yok.</div>
        <div className="text-xs text-neutral-400 mt-2">Personel yükledikten sonra &quot;Adresleri Haritada Konumla&quot; butonuna basın.</div>
      </div>
    );
  }

  return (
    <div className="relative" style={{ height }}>
      <MapContainer center={center} zoom={11} scrollWheelZoom style={{ height, width: "100%", borderRadius: 6 }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <RectSelect enabled={selectMode} onSelectBounds={selectInBounds} />
        {visible.map((p) => {
          const isSel = selected.has(p.id);
          const icon = isSel ? selectedIcon(colorFor(p.company_name)) : colorIcon(colorFor(p.company_name));
          return (
            <Marker
              key={p.id}
              position={[p.lat, p.lng]}
              icon={icon}
              eventHandlers={selectMode ? { click: () => togglePerson(p.id) } : {}}
            >
              {!selectMode && (
                <Popup>
                  <div className="text-xs">
                    <div className="font-semibold">{p.first_name} {p.last_name}</div>
                    <div className="text-neutral-600">{p.company_name}</div>
                    <div className="text-neutral-500 mt-1">{p.district}{p.city ? ` — ${p.city}` : ""}</div>
                    <div className="text-neutral-500">{p.address}</div>
                    {p.pickup_time && <div className="mt-1">Alım: <b>{p.pickup_time}</b></div>}
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      {onFix && (
                        <button
                          data-testid={`map-fix-${p.id}`}
                          onClick={() => onFix(p)}
                          style={{ padding: "4px 10px", background: "#0020C2", color: "#fff", border: "none", cursor: "pointer", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}
                        >
                          Konumu Düzelt
                        </button>
                      )}
                      {onDelete && (
                        <button
                          data-testid={`map-delete-${p.id}`}
                          onClick={() => onDelete(p)}
                          style={{ padding: "4px 10px", background: "#fff", color: "#DC2626", border: "1px solid #DC2626", cursor: "pointer", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}
                        >
                          Sil
                        </button>
                      )}
                    </div>
                  </div>
                </Popup>
              )}
            </Marker>
          );
        })}
      </MapContainer>

      {/* Toplu seçim modu düğmesi */}
      {onBulkCompany && (
        <button
          data-testid="map-bulk-select-toggle"
          onClick={toggleSelectMode}
          title="Haritadan personel seçip firmalarını topluca değiştirin"
          className={`absolute top-3 left-14 z-[500] flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-widest font-bold border shadow-sm transition-colors ${
            selectMode ? "bg-[#0020C2] text-white border-[#0020C2]" : "bg-white/95 backdrop-blur-sm border-[#E5E5E5] hover:bg-[#F5F5F5]"
          }`}
        >
          <BoxSelect className="w-3.5 h-3.5" /> {selectMode ? "Seçim Modu Açık" : "Toplu Seçim"}
        </button>
      )}

      {/* Seçim modu alt barı — firma değiştir */}
      {selectMode && (
        <div
          data-testid="map-bulk-bar"
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[500] bg-white border border-[#0020C2] shadow-lg px-3 py-2 flex items-center gap-2 flex-wrap max-w-[95%]"
        >
          <span className="text-xs font-bold whitespace-nowrap">
            <span className="num text-[#0020C2]">{selected.size}</span> personel seçildi
          </span>
          <span className="text-[10px] text-neutral-400 hidden md:inline">— tıkla veya alan çiz</span>
          <select
            data-testid="map-bulk-company-select"
            value={targetCompany}
            onChange={(e) => setTargetCompany(e.target.value)}
            className="text-xs border border-[#E5E5E5] px-2 py-1.5 bg-white cursor-pointer max-w-[160px]"
          >
            <option value="">Firma seç...</option>
            {companies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input
            data-testid="map-bulk-company-new"
            value={newCompany}
            onChange={(e) => setNewCompany(e.target.value)}
            placeholder="veya yeni firma adı"
            className="text-xs border border-[#E5E5E5] px-2 py-1.5 w-[140px]"
          />
          <button
            data-testid="map-bulk-apply"
            onClick={applyCompany}
            disabled={applying || selected.size === 0 || !(newCompany.trim() || targetCompany)}
            className="flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-40"
          >
            <Check className="w-3 h-3" /> {applying ? "Uygulanıyor..." : "Firmayı Değiştir"}
          </button>
          {selected.size > 0 && (
            <button
              data-testid="map-bulk-clear"
              onClick={() => setSelected(new Set())}
              className="px-2 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]"
            >
              Temizle
            </button>
          )}
          <button
            data-testid="map-bulk-exit"
            onClick={toggleSelectMode}
            title="Seçim modundan çık"
            className="p-1.5 hover:bg-[#F5F5F5]"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Firma filtresi (tıkla: gizle/göster · çift tıkla: sadece bu firma) */}
      {companies.length > 0 && (
        <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm border border-[#E5E5E5] p-3 rounded-md z-[500] shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Firmalar</div>
          <div className="space-y-1">
            {companies.map(c => {
              const off = hiddenCompanies.has(c);
              const count = withCoords.filter(p => p.company_name === c).length;
              return (
                <button
                  key={c}
                  data-testid={`map-company-toggle-${c}`}
                  onClick={() => toggleCompany(c)}
                  onDoubleClick={() => showOnly(c)}
                  title="Tıkla: gizle/göster · Çift tıkla: sadece bu firma"
                  className={`w-full flex items-center gap-2 text-xs transition-opacity ${off ? "opacity-35 line-through" : "hover:opacity-75"}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colorFor(c) }} />
                  <span className="truncate max-w-[140px] text-left">{c}</span>
                  <span className="ml-auto text-[10px] text-neutral-400 num">{count}</span>
                </button>
              );
            })}
          </div>
          {hiddenCompanies.size > 0 && (
            <button onClick={() => setHiddenCompanies(new Set())} className="mt-2 text-[10px] uppercase tracking-widest font-bold text-[#0020C2] hover:underline">
              Tümünü Göster
            </button>
          )}
        </div>
      )}
    </div>
  );
}
